import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Spinner, Nav, OverlayTrigger, Tooltip, Modal, Form } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { NOILAYSOLIEU_API, QUATRINHLAYSOLIEU_API, DUYET_NOILAYSOLIEU_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { trangThaiLaySoLieuValue, objectTrangThaiLaySoLieu } from '../../../../utils/define';
import { Get, DeleteSingle, Put } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import moment from 'moment';
import QuaTrinhLaySoLieuAdd from './QuaTrinhLaySoLieuAdd';
import QuaTrinhLaySoLieuEdit from './QuaTrinhLaySoLieuEdit';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import DocumentInput from '../../../../components/document-input/DocumentInput';

const QuaTrinhLaySoLieu = ({ hoSoId, activeTab, setActiveTab, isAdmin }) => {
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit', 'view'
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [idQuaTrinhWantDelete, setIdQuaTrinhWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    // Duyet state
    const [showDuyetModal, setShowDuyetModal] = useState(false);
    const [trangThai, setTrangThai] = useState('');
    const [ghiChu, setGhiChu] = useState('');
    const [fileCongVan, setFileCongVan] = useState('');
    const [isSavingDuyet, setIsSavingDuyet] = useState(false);

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const { isLoading: isLoadingNoiLay, data: queryDataNoiLay, refetch: refetchNoiLay } = useQuery(
        ['noiLaySoLieuData', hoSoId],
        () => Get(`${NOILAYSOLIEU_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId && viewMode === 'list',
            keepPreviousData: true
        }
    );

    const { isLoading: isLoadingQuaTrinh, data: queryDataQuaTrinh, refetch: refetchQuaTrinh } = useQuery(
        ['quaTrinhLaySoLieuData', hoSoId],
        () => Get(QUATRINHLAYSOLIEU_API.GET_PAGING, null, { HoSoNghienCuuSinhId: hoSoId, PageIndex: 1, PageSize: 100 }),
        { 
            enabled: !!hoSoId && viewMode === 'list',
            keepPreviousData: true
        }
    );

    // Normalize data
    let dataListNoiLay = [];
    if (queryDataNoiLay) {
        if (Array.isArray(queryDataNoiLay)) dataListNoiLay = queryDataNoiLay;
        else if (queryDataNoiLay.data && Array.isArray(queryDataNoiLay.data)) dataListNoiLay = queryDataNoiLay.data;
        else if (queryDataNoiLay.$values) dataListNoiLay = queryDataNoiLay.$values;
        else if (queryDataNoiLay.data && queryDataNoiLay.data.$values) dataListNoiLay = queryDataNoiLay.data.$values;
        else dataListNoiLay = [queryDataNoiLay];
    }

    let dataListQuaTrinh = [];
    if (queryDataQuaTrinh) {
        if (Array.isArray(queryDataQuaTrinh)) dataListQuaTrinh = queryDataQuaTrinh;
        else if (queryDataQuaTrinh.data && Array.isArray(queryDataQuaTrinh.data)) dataListQuaTrinh = queryDataQuaTrinh.data;
        else if (queryDataQuaTrinh.data?.$values) dataListQuaTrinh = queryDataQuaTrinh.data.$values;
        else if (queryDataQuaTrinh.$values) dataListQuaTrinh = queryDataQuaTrinh.$values;
    }

    // Combine data by index
    const combinedData = dataListNoiLay.map((noiLay, index) => {
        return {
            noiLaySoLieu: noiLay,
            quaTrinhLaySoLieu: dataListQuaTrinh[index] || null
        };
    });

    useEffect(() => {
        if (combinedData && combinedData.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = combinedData.find(item => item.noiLaySoLieu?.id === prev.noiLaySoLieu?.id);
                    return fresh || combinedData[0];
                }
                return combinedData[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [queryDataNoiLay, queryDataQuaTrinh]); // Depend on queries

    const refreshData = () => {
        queryClient.invalidateQueries(['noiLaySoLieuData', hoSoId]);
        queryClient.invalidateQueries(['quaTrinhLaySoLieuData', hoSoId]);
        refetchNoiLay();
        refetchQuaTrinh();
    };

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const handleView = (item) => {
        setDataEdit(item);
        setViewMode('view');
    };

    const handleOpenDuyet = (item) => {
        setDataEdit(item);
        setTrangThai(item.noiLaySoLieu?.trangThai || '');
        setGhiChu(item.noiLaySoLieu?.ghiChuChinhSua || '');
        setFileCongVan(item.noiLaySoLieu?.fileCongVan || '');
        setShowDuyetModal(true);
    };

    const submitDuyet = async () => {
        if (!trangThai) {
            toast.warning('Vui lòng chọn trạng thái!');
            return;
        }

        const payload = {
            HoSoNghienCuuSinhId: hoSoId,
            NoiLay: dataEdit.noiLaySoLieu?.noiLay || '',
            FileCuDiLay: dataEdit.noiLaySoLieu?.fileCuDiLay || '',
            FileCongVan: fileCongVan || '',
            TrangThai: trangThai,
            GhiChuChinhSua: ghiChu
        };

        setIsSavingDuyet(true);
        try {
            const res = await Put(DUYET_NOILAYSOLIEU_API.DUYET_HO_SO, dataEdit.noiLaySoLieu?.id, payload);
            if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id || res.success)) {
                toast.success('Lưu trạng thái thành công!');
                refreshData();
                setShowDuyetModal(false);
            } else {
                toast.error(res?.message || 'Có lỗi xảy ra');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra');
        } finally {
            setIsSavingDuyet(false);
        }
    };

    const deleteAction = (item) => {
        setIdWantDelete(item.noiLaySoLieu?.id);
        setIdQuaTrinhWantDelete(item.quaTrinhLaySoLieu?.id);
        setConfirmDelMessage('Quá trình lấy số liệu này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (!idWantDelete) return;

        try {
            // Delete NoiLaySoLieu
            await DeleteSingle(NOILAYSOLIEU_API.DELETE_ITEM, idWantDelete, null);
            
            // Delete QuaTrinhLaySoLieu if exists
            if (idQuaTrinhWantDelete) {
                await DeleteSingle(QUATRINHLAYSOLIEU_API.DELETE_ITEM, idQuaTrinhWantDelete, null);
            }

            toast.success('Xóa thành công');
            refreshData();
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra khi xóa');
        }

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
        setIdQuaTrinhWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setIdQuaTrinhWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiLaySoLieuValue.choTiepNhan: return <Badge variant="warning">Chờ tiếp nhận</Badge>;
            case trangThaiLaySoLieuValue.tiepNhanHoSo: return <Badge variant="info">Tiếp nhận hồ sơ</Badge>;
            case trangThaiLaySoLieuValue.chinhSua: return <Badge variant="secondary">Yêu cầu chỉnh sửa</Badge>;
            case trangThaiLaySoLieuValue.dangLaySoLieu: return <Badge variant="primary">Đang lấy số liệu</Badge>;
            case trangThaiLaySoLieuValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            case trangThaiLaySoLieuValue.tuChoi: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100 card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-row">
                        <Nav.Item>
                            <Nav.Link eventKey="quaTrinhLaySoLieu" className="font-weight-bold">Quá trình lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="kiemTraSoLieu" className="font-weight-bold">Kiểm tra số liệu</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    {!isAdmin && (
                        <Button variant="success" size="sm" onClick={handleAdd}>
                            <i className="fas fa-plus mr-1"></i> Thêm mới
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Card.Body>
                {isLoadingNoiLay || isLoadingQuaTrinh ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive flex-grow-1">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" width="50">STT</th>
                                        <th>Nơi lấy số liệu</th>
                                        <th className="text-center">Từ ngày</th>
                                        <th className="text-center">Đến ngày</th>
                                        <th className="text-center">Đã đạt</th>
                                        {/* <th className="text-center">File đính kèm</th> */}
                                        <th className="text-center">File công văn</th>
                                        <th className="text-center">Trạng thái</th>
                                        <th>Ghi chú</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {combinedData && combinedData.length > 0 ? (
                                    combinedData.map((item, index) => {
                                        const nl = item.noiLaySoLieu;
                                        const qt = item.quaTrinhLaySoLieu;
                                        if (!nl) return null;
                                        return (
                                            <tr 
                                                key={nl.id}
                                                onClick={() => setSelectedRow(item)}
                                                style={{ cursor: 'pointer' }}
                                                className={selectedRow?.noiLaySoLieu?.id === nl.id ? 'table-primary' : ''}
                                            >
                                                <td className="text-center">{index + 1}</td>
                                                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{nl.noiLay}</td>
                                                <td className="text-center">
                                                    {qt?.tuNgay ? moment(qt.tuNgay).format('DD/MM/YYYY') : ''}
                                                </td>
                                                <td className="text-center">
                                                    {qt?.denNgay ? moment(qt.denNgay).format('DD/MM/YYYY') : ''}
                                                </td>
                                                <td className="text-center">
                                                    {qt?.mauDatTyLe !== null && qt?.mauDatTyLe !== undefined ? `${qt.mauDatTyLe}%` : ''}
                                                </td>
                                                {/* <td className="text-center">
                                                    {nl.fileCuDiLay ? (
                                                        (() => {
                                                            const path = nl.fileCuDiLay;
                                                            const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                            const safePath = path.startsWith('/') ? path : `/${path}`;
                                                            const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                            let name = path.split('/').pop() || 'File';
                                                            name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                            return (
                                                                <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name} onClick={(e) => e.stopPropagation()}>
                                                                    <i className="fas fa-paperclip mr-1"></i> {name}
                                                                </a>
                                                            );
                                                        })()
                                                    ) : (
                                                        <span className="text-muted">Không có</span>
                                                    )}
                                                </td> */}
                                                <td className="text-center">
                                                    {nl.fileCongVan ? (
                                                        (() => {
                                                            const path = nl.fileCongVan;
                                                            const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                            const safePath = path.startsWith('/') ? path : `/${path}`;
                                                            const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                            let name = path.split('/').pop() || 'File';
                                                            name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                            return (
                                                                <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-info btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name} onClick={(e) => e.stopPropagation()}>
                                                                    <i className="fas fa-file-alt mr-1"></i> {name}
                                                                </a>
                                                            );
                                                        })()
                                                    ) : (
                                                        <span className="text-muted">Chờ HLĐT gửi</span>
                                                    )}
                                                </td>
                                                <td className="text-center">{renderTrangThai(nl.trangThai)}</td>
                                                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{nl.ghiChuChinhSua}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted">
                                            Chưa có dữ liệu Quá trình lấy số liệu
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        </div>
                        <ConfirmModal
                            isOpen={openConfirmDeleteModal}
                            confirmType="delete"
                            goBackAfterDelete={false}
                            handleAccept={acceptDelete}
                            handleCancel={cancelDelete}
                            content={confirmDelMessage}
                        />
                    </>
                )}
            </Card.Body>
            {!(isLoadingNoiLay || isLoadingQuaTrinh) && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="primary" 
                            size="sm" 
                            onClick={() => handleView(selectedRow)} 
                            className="mr-2"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-eye mr-1"></i> Xem chi tiết
                        </Button>
                        
                        {isAdmin && (
                            <Button 
                                variant="warning" 
                                size="sm" 
                                onClick={() => handleOpenDuyet(selectedRow)} 
                                className="mr-2 text-white"
                                disabled={!selectedRow}
                            >
                                <i className="fas fa-tasks mr-1"></i> Cập nhật trạng thái
                            </Button>
                        )}
                        
                        <Button 
                            variant="info" 
                            size="sm" 
                            className="mr-2" 
                            onClick={() => handleEdit(selectedRow)}
                            disabled={!selectedRow || (!isAdmin && (selectedRow?.noiLaySoLieu?.trangThai !== trangThaiLaySoLieuValue.choTiepNhan && selectedRow?.noiLaySoLieu?.trangThai !== trangThaiLaySoLieuValue.chinhSua))}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>

                        {!isAdmin && (
                            <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => deleteAction(selectedRow)}
                                disabled={!selectedRow || (selectedRow?.noiLaySoLieu?.trangThai !== trangThaiLaySoLieuValue.choTiepNhan && selectedRow?.noiLaySoLieu?.trangThai !== trangThaiLaySoLieuValue.chinhSua)}
                            >
                                <i className="fas fa-trash mr-1"></i> Xóa
                            </Button>
                        )}
                    </div>
                </Card.Footer>
            )}
        </Card>

        {viewMode === 'add' && <QuaTrinhLaySoLieuAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refreshData} />}
        {viewMode === 'edit' && dataEdit && <QuaTrinhLaySoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refreshData} isView={false} isAdmin={isAdmin} />}
        {viewMode === 'view' && dataEdit && <QuaTrinhLaySoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refreshData} isView={true} isAdmin={isAdmin} />}

        <Modal show={showDuyetModal} onHide={() => setShowDuyetModal(false)} backdrop="static" keyboard={false} size="md">
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>
                    Cập nhật trạng thái
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group>
                        <Form.Label>Trạng thái <span className="text-danger">*</span></Form.Label>
                        <Form.Control as="select" value={trangThai} onChange={(e) => setTrangThai(e.target.value)}>
                            {objectTrangThaiLaySoLieu.map((st) => (
                                <option key={st.value} value={st.value}>{st.label}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Ghi chú {trangThai === trangThaiLaySoLieuValue.tuChoi || trangThai === trangThaiLaySoLieuValue.chinhSua ? <span className="text-danger">*</span> : ''}</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            placeholder="Nhập ghi chú (nếu có)..." 
                            value={ghiChu}
                            onChange={(e) => setGhiChu(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group>
                        <DocumentInput
                            labelTitle="File công văn đính kèm"
                            value={fileCongVan}
                            onChangeProp={(url) => setFileCongVan(url)}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDuyetModal(false)} disabled={isSavingDuyet}>
                    Hủy
                </Button>
                <Button variant="primary" onClick={submitDuyet} disabled={isSavingDuyet || ((trangThai === trangThaiLaySoLieuValue.tuChoi || trangThai === trangThaiLaySoLieuValue.chinhSua) && !ghiChu.trim())}>
                    {isSavingDuyet ? 'Đang lưu...' : 'Lưu lại'}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default QuaTrinhLaySoLieu;
